/* Pillow finder controller. Static and cacheable; Liquid data lives in JSON islands. */
(function () {
  'use strict';

  /* Falls back to a literal default (not just {}) so an already-cached
     page whose theme-strings script predates this key still shows correct
     copy instead of "undefined" — same defensive merge theme-core.js and
     cart-drawer.js already use for their own fallback strings. */
  var themeStrings = Object.assign({
    finderAddedRedirecting: 'Added to bag. Redirecting to your bag…'
  }, (window.KPTheme && window.KPTheme.strings) || {});

  function initPillowFinder(wrapper) {
    if (!wrapper || wrapper.dataset.pillowFinderInitialized === 'true') return;
    wrapper.dataset.pillowFinderInitialized = 'true';

    var finderImages = {};
    try {
      var imageData = wrapper.querySelector('[data-finder-images]');
      if (imageData) finderImages = JSON.parse(imageData.textContent);
    } catch (error) {
      console.error('Failed to parse pillow finder image data', error);
    }

  const storeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function track(name, properties) {
    if (window.KPTheme && typeof window.KPTheme.track === 'function') {
      window.KPTheme.track(name, properties || {});
    }
  }

  function fetchWithTimeout(url, options, timeout) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout || 6000);
    return fetch(url, Object.assign({}, options, { signal: controller.signal }))
      .finally(() => window.clearTimeout(timer));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function safeUrl(value, fallback) {
    if (!value) return fallback || '#';
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return fallback || '#';
      return escapeHtml(url.href);
    } catch (error) {
      return fallback || '#';
    }
  }
  
  let productData = [];
  const fallbackCatalogHandles = [
    'putnam-pillow',
    'putnam-memory-foam-pillow',
    'putnam-travel-pillow',
    'butterfly-pillow',
    'cervical-roll',
    'face-pillow',
    'knee-pillow',
    'memory-foam-contour-pillow',
    'bed-wedge',
    'leg-rest',
    '8-wedge',
    '8-wedge-coccyx-cut-out',
    '11-wedge-coccyx-cut-out',
    'putnam-wedge',
    'ring-cushion',
    'memory-foam-ring-cushion',
    'comfort-cushion',
    'rest-a-back',
    'rest-a-head',
    'lumbar-supports',
    'superest',
    'large-backrest',
    'compact-backrest',
    'original-cpap-pillow',
    'advanced-cpap-pillow',
    'memory-foam-advanced-cpap-pillow',
    'pregnancy-labour-birthing-cushion',
    'travel-cpap-pillow',
    'bariactric-cushion',
    'memory-foam-travel-bed-wedge',
    't-roll-positioning-aid',
    'putnam-self-adjusting-pillow',
    'graphite-latex-pillow-height-adjustable',
    'british-wool-knee-pillow-natural',
    'heel-lift-pillow',
    'orthopaedic-hip-abduction-wedge-velcro-straps',
    'lumbar-support-d-shape-bed-pillow',
    'postpartum-ring-cushion-memory-foam-donut',
    'kneeling-chair',
    'pregnancy-cuddle-pillow',
    'graphite-latex-pillow',
    'sero-pressure-ring-cushion-1',
    'u-shaped-pregnancy-pillow'
  ];
  const catalogHandles = Array.isArray(window.PF_CATALOG_HANDLES) && window.PF_CATALOG_HANDLES.length
    ? window.PF_CATALOG_HANDLES
    : fallbackCatalogHandles;
  const productCoverageMap = {
    'putnam-pillow': { branches: ['neck'], priority: 9, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'back', material: 'soft' }, { primaryNeed: 'neck', sleepPosition: 'side', material: 'soft' }] },
    'putnam-memory-foam-pillow': { branches: ['neck'], priority: 12, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'side', material: 'memory_foam' }, { primaryNeed: 'neck', sleepPosition: 'side', material: 'any' }] },
    'putnam-travel-pillow': { branches: ['neck'], features: ['travel'], priority: 8, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'mixed', material: 'travel' }] },
    'butterfly-pillow': { branches: ['specialist', 'neck'], features: ['face_support'], priority: 8, winPaths: [{ primaryNeed: 'specialist', specialistType: 'stomach_sleeper' }, { primaryNeed: 'neck', sleepPosition: 'front' }] },
    'cervical-roll': { branches: ['neck'], priority: 9, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'back', material: 'roll' }] },
    'face-pillow': { branches: ['specialist', 'neck'], features: ['face_support'], priority: 10, winPaths: [{ primaryNeed: 'specialist', specialistType: 'stomach_sleeper' }, { primaryNeed: 'neck', sleepPosition: 'front' }] },
    'knee-pillow': { branches: ['body'], priority: 12, winPaths: [{ primaryNeed: 'body', bodyArea: 'knee', bodyFeature: 'memory_foam' }, { primaryNeed: 'body', bodyArea: 'knee', bodyFeature: 'any' }] },
    'memory-foam-contour-pillow': { branches: ['neck'], features: ['contour'], priority: 10, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'back', material: 'memory_foam' }] },
    'bed-wedge': { branches: ['body', 'specialist'], features: ['wedge'], priority: 12, winPaths: [{ primaryNeed: 'body', bodyArea: 'upper_body' }, { primaryNeed: 'specialist', specialistType: 'reflux' }] },
    'leg-rest': { branches: ['body'], features: ['leg_elevation'], priority: 11, winPaths: [{ primaryNeed: 'body', bodyArea: 'body' }] },
    '8-wedge': { branches: ['sitting'], features: ['wedge', 'posture'], priority: 10, winPaths: [{ primaryNeed: 'sitting', sittingType: 'posture', sittingFeature: 'wedge' }] },
    '8-wedge-coccyx-cut-out': { branches: ['sitting'], features: ['seat_relief', 'wedge'], priority: 10, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'coccyx_wedge' }] },
    '11-wedge-coccyx-cut-out': { branches: ['sitting'], features: ['seat_relief', 'wedge'], priority: 8, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'coccyx_wedge' }] },
    'putnam-wedge': { branches: ['sitting'], features: ['posture', 'wedge'], priority: 9, winPaths: [{ primaryNeed: 'sitting', sittingType: 'posture', sittingFeature: 'wedge' }] },
    'ring-cushion': { branches: ['sitting'], features: ['seat_relief'], priority: 10, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'ring' }] },
    'memory-foam-ring-cushion': { branches: ['sitting'], features: ['seat_relief'], priority: 9, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'ring' }] },
    'comfort-cushion': { branches: ['sitting'], features: ['seat_relief', 'portable'], priority: 9, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'booster' }] },
    'rest-a-back': { branches: ['sitting'], features: ['lumbar'], priority: 10, winPaths: [{ primaryNeed: 'sitting', sittingType: 'lumbar', sittingFeature: 'backrest' }] },
    'rest-a-head': { branches: ['sitting'], features: ['portable', 'posture'], priority: 8, winPaths: [{ primaryNeed: 'sitting', sittingType: 'posture', sittingFeature: 'any' }] },
    'lumbar-supports': { branches: ['sitting'], features: ['lumbar'], priority: 10, winPaths: [{ primaryNeed: 'sitting', sittingType: 'lumbar', sittingFeature: 'roll' }] },
    'superest': { branches: ['sitting'], features: ['lumbar', 'posture'], priority: 9, winPaths: [{ primaryNeed: 'sitting', sittingType: 'lumbar', sittingFeature: 'backrest' }] },
    'large-backrest': { branches: ['sitting'], features: ['lumbar', 'backrest'], priority: 9, winPaths: [{ primaryNeed: 'sitting', sittingType: 'lumbar', sittingFeature: 'backrest' }] },
    'compact-backrest': { branches: ['sitting'], features: ['lumbar', 'backrest', 'portable'], priority: 9, winPaths: [{ primaryNeed: 'sitting', sittingType: 'lumbar', sittingFeature: 'roll' }] },
    'lumbar-support-d-shape-bed-pillow': { branches: ['sitting', 'specialist'], features: ['lumbar'], priority: 8, winPaths: [{ primaryNeed: 'sitting', sittingType: 'lumbar', sittingFeature: 'any' }, { primaryNeed: 'specialist', specialistType: 'hip_surgery' }] },
    'original-cpap-pillow': { branches: ['specialist'], features: ['contour'], priority: 9, winPaths: [{ primaryNeed: 'specialist', specialistType: 'cpap', specialistFeature: 'home' }] },
    'advanced-cpap-pillow': { branches: ['specialist'], features: ['contour'], priority: 12, winPaths: [{ primaryNeed: 'specialist', specialistType: 'cpap', specialistFeature: 'home' }, { primaryNeed: 'specialist', specialistType: 'cpap', specialistFeature: 'any' }] },
    'memory-foam-advanced-cpap-pillow': { branches: ['specialist'], features: ['contour'], priority: 11, winPaths: [{ primaryNeed: 'specialist', specialistType: 'cpap', specialistFeature: 'home' }] },
    'pregnancy-labour-birthing-cushion': { branches: ['pregnancy'], features: ['positioning', 'labour'], priority: 8, winPaths: [{ primaryNeed: 'pregnancy', pregnancyType: 'sleep' }] },
    'travel-cpap-pillow': { branches: ['specialist'], features: ['travel'], priority: 9, winPaths: [{ primaryNeed: 'specialist', specialistType: 'cpap', specialistFeature: 'travel' }] },
    'bariactric-cushion': { branches: ['sitting'], features: ['seat_relief'], priority: 8, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'ring' }] },
    'memory-foam-travel-bed-wedge': { branches: ['body', 'specialist'], features: ['wedge', 'travel'], priority: 9, winPaths: [{ primaryNeed: 'body', bodyArea: 'upper_body' }, { primaryNeed: 'specialist', specialistType: 'reflux' }] },
    't-roll-positioning-aid': { branches: ['body', 'specialist'], features: ['positioning', 'leg_elevation'], priority: 9, winPaths: [{ primaryNeed: 'body', bodyArea: 'body' }, { primaryNeed: 'specialist', specialistType: 'hip_surgery' }] },
    'putnam-self-adjusting-pillow': { branches: ['neck'], features: ['height_adjustable'], priority: 9, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'mixed', material: 'adjustable' }] },
    'graphite-latex-pillow-height-adjustable': { branches: ['neck'], materials: ['natural'], features: ['cooling', 'height_adjustable'], priority: 11, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'mixed', material: 'natural' }, { primaryNeed: 'neck', sleepPosition: 'back', material: 'natural' }] },
    'orthopaedic-hip-abduction-wedge-velcro-straps': { branches: ['specialist', 'body'], features: ['positioning', 'leg_elevation'], priority: 11, winPaths: [{ primaryNeed: 'specialist', specialistType: 'hip_surgery' }, { primaryNeed: 'body', bodyArea: 'body' }] },
    'british-wool-knee-pillow-natural': { branches: ['body'], materials: ['natural', 'soft'], priority: 10, winPaths: [{ primaryNeed: 'body', bodyArea: 'knee', bodyFeature: 'natural' }] },
    'heel-lift-pillow': { branches: ['body'], features: ['leg_elevation'], priority: 10, winPaths: [{ primaryNeed: 'body', bodyArea: 'body' }] },
    'postpartum-ring-cushion-memory-foam-donut': { branches: ['pregnancy', 'sitting'], features: ['seat_relief'], priority: 10, winPaths: [{ primaryNeed: 'pregnancy', pregnancyType: 'sitting_recovery' }] },
    'kneeling-chair': { branches: ['sitting'], features: ['posture'], priority: 10, winPaths: [{ primaryNeed: 'sitting', sittingType: 'posture', sittingFeature: 'kneeling' }] },
    'pregnancy-cuddle-pillow': { branches: ['pregnancy'], features: ['full_body', 'body_hug'], priority: 9, winPaths: [{ primaryNeed: 'pregnancy', pregnancyType: 'sleep' }] },
    'graphite-latex-pillow': { branches: ['neck'], materials: ['natural'], features: ['cooling'], priority: 11, winPaths: [{ primaryNeed: 'neck', sleepPosition: 'side', material: 'natural' }] },
    'sero-pressure-ring-cushion-1': { branches: ['sitting'], features: ['seat_relief'], priority: 8, winPaths: [{ primaryNeed: 'sitting', sittingType: 'seat', sittingFeature: 'ring' }] },
    'u-shaped-pregnancy-pillow': { branches: ['pregnancy'], features: ['full_body', 'body_hug'], priority: 12, winPaths: [{ primaryNeed: 'pregnancy', pregnancyType: 'sleep' }] }
  };
  const emergencyProducts = [
    { handle: 'putnam-memory-foam-pillow', title: 'Putnams Memory Foam Orthopedic Contour Pillow', url: '/products/putnam-memory-foam-pillow', image: '', price: '', summary: 'Firm contour support for neck and shoulder alignment.', useCases: ['neck'], positions: ['side', 'back', 'mixed'], materials: ['memory_foam'], features: ['contour'], priority: 10, variantOptions: [] },
    { handle: 'knee-pillow', title: 'Adjustable Knee Pillow for Side Sleepers', url: '/products/knee-pillow', image: '', price: '', summary: 'Core knee separator pillow for hip and spine alignment.', useCases: ['body'], positions: ['side', 'mixed'], materials: ['memory_foam', 'adjustable'], features: ['knee_alignment', 'height_adjustable'], priority: 10, variantOptions: [] },
    { handle: 'bed-wedge', title: 'Bed Wedge Pillow', url: '/products/bed-wedge', image: '', price: '', summary: 'Upper body elevation for reflux and snoring support.', useCases: ['body'], positions: ['back', 'mixed'], materials: ['memory_foam'], features: [], priority: 10, variantOptions: [] },
    { handle: 'u-shaped-pregnancy-pillow', title: 'Full Body Pregnancy Pillow UK – U-Shaped Maternity Support', url: '/products/u-shaped-pregnancy-pillow', image: '', price: '', summary: 'Long full-body support for side sleeping during pregnancy.', useCases: ['pregnancy', 'body'], positions: ['side', 'mixed'], materials: ['soft'], features: ['full_body', 'body_hug'], priority: 10, variantOptions: [] },
    { handle: 'advanced-cpap-pillow', title: 'Advanced CPAP Pillow', url: '/products/advanced-cpap-pillow', image: '', price: '', summary: 'Contour CPAP support to reduce mask pressure and leaks.', useCases: ['specialist'], positions: ['side', 'back', 'mixed'], materials: ['soft'], features: ['contour'], priority: 10, variantOptions: [] }
  ];
  try {
    const productJson = wrapper.querySelector('[data-product-data]');
    if (productJson) {
      productData = JSON.parse(productJson.textContent || '[]');
    }
  } catch (error) {
    console.error('Pillow finder data parse failed', error);
    productData = [];
  }
  if (!Array.isArray(productData) || productData.length === 0) {
    productData = emergencyProducts;
  }

  function titleFromHandle(handle) {
    return handle.replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  function inferMeta(handle) {
    const h = (handle || '').toLowerCase();
    const useCases = [];
    const positions = ['mixed'];
    const materials = [];
    const features = [];
    let priority = 7;
    let summary = 'Targeted support to improve comfort and alignment.';
    const isSeatStyle = h.includes('seat') || h.includes('ring') || h.includes('coccyx') || h.includes('cushion');
    const isBedOrLegElevation =
      h.includes('bed-wedge') ||
      h.includes('travel-bed-wedge') ||
      h.includes('leg-rest') ||
      h.includes('heel') ||
      h.includes('elevat') ||
      h.includes('reflux') ||
      (h.includes('wedge') && !isSeatStyle);

    if (h.includes('specialist')) {
      useCases.push('specialist');
      positions.push('side', 'back');
      summary = 'CPAP-focused support designed to reduce mask pressure and improve comfort.';
      priority = 10;
    }
    if (h.includes('pregnancy') || h.includes('maternity') || h.includes('pregnancy-labour-birthing-cushion') || h.includes('postpartum') || h.includes('total-body')) {
      useCases.push('pregnancy');
      positions.push('side');
      summary = 'Pregnancy support option for bump comfort, side sleeping, and maternity positioning.';
      priority = 10;
    }
    if ((h.includes('knee') && !h.includes('kneeling')) || h.includes('leg') || h.includes('heel')) {
      useCases.push('body');
      positions.push('side', 'back');
      features.push('knee_alignment', 'leg_elevation');
      summary = 'Leg and knee support to improve hip alignment and reduce pressure.';
      priority = Math.max(priority, 9);
    }
    if (isBedOrLegElevation) {
      useCases.push('body');
      positions.push('back');
      summary = 'Elevation support for reflux, snoring, or recovery comfort.';
      priority = Math.max(priority, 9);
    }
    if (
      h.includes('seat') ||
      h.includes('ring') ||
      h.includes('lumbar') ||
      h.includes('backrest') ||
      h.includes('coccyx') ||
      h.includes('cushion') ||
      h.includes('rest-a-back')
    ) {
      useCases.push('sitting');
      if (h.includes('ring') || h.includes('coccyx') || h.includes('seat') || h.includes('cushion')) {
        features.push('seat_relief');
      }
      if (h.includes('lumbar') || h.includes('backrest') || h.includes('rest-a-back')) {
        features.push('lumbar');
      }
      summary = 'Seated comfort and posture support for work, driving, and pressure relief.';
      priority = Math.max(priority, 8);
    }
    if (h.includes('rest-a-head')) {
      useCases.push('sitting');
      features.push('posture', 'portable');
      summary = 'Portable head and posture support for sitting.';
      priority = Math.max(priority, 8);
    }
    if (h.includes('specialist') || h.includes('face') || h.includes('facial') || h.includes('surgery') || h.includes('butterfly')) {
      useCases.push('specialist');
      summary = 'Face-friendly support to reduce pressure points and improve comfort while resting.';
      priority = Math.max(priority, 9);
    }
    if (h.includes('kneeling-chair') || h.includes('kneeling')) {
      useCases.push('sitting');
      features.push('posture');
      summary = 'Posture-focused seating support for desk and home-office use.';
      priority = Math.max(priority, 8);
    }
    if (
      h.includes('cervical') ||
      h.includes('contour') ||
      (h.includes('neck') && !h.includes('kneeling')) ||
      h === 'putnam-pillow' ||
      h === 'putnam-memory-foam-pillow' ||
      h === 'putnam-travel-pillow' ||
      h === 'putnam-self-adjusting-pillow' ||
      h.includes('graphite-latex')
    ) {
      useCases.push('neck');
      summary = summary === 'Targeted support to improve comfort and alignment.'
        ? 'Neck and shoulder support for better sleep alignment.'
        : summary;
      priority = Math.max(priority, 8);
    }

    if (h.includes('memory-foam') || h.includes('memory')) materials.push('memory_foam');
    if (h.includes('wool') || h.includes('latex') || h.includes('organic') || h.includes('natural')) materials.push('natural');
    if (h.includes('adjust') || h.includes('hull')) materials.push('adjustable');
    if (!materials.length) materials.push('soft');

    if (h.includes('travel')) features.push('travel');
    if (h.includes('cooling') || h.includes('graphite')) features.push('cooling');
    if (h.includes('adjust') || h.includes('height')) features.push('height_adjustable');
    if (h.includes('full-body') || h.includes('u-shaped') || h.includes('total-body')) features.push('full_body', 'body_hug');
    if (h.includes('pregnancy-labour-birthing-cushion') || h.includes('positioning') || h.includes('t-roll')) features.push('positioning');
    if (h.includes('contour') || h.includes('specialist')) features.push('contour');
    if (h.includes('face') || h.includes('specialist') || h.includes('facial')) features.push('face_support');

    return {
      handle: handle,
      title: titleFromHandle(handle),
      url: `/products/${handle}`,
      image: '',
      price: '',
      summary,
      useCases: Array.from(new Set(useCases.length ? useCases : ['neck'])),
      positions: Array.from(new Set(positions)),
      materials: Array.from(new Set(materials)),
      features: Array.from(new Set(features)),
      priority,
      variantOptions: []
    };
  }

  function matchesWinPath(path, answers) {
    return Object.keys(path).every((key) => answers[key] === path[key]);
  }

  function mergeCatalogData() {
    const byHandle = {};
    (productData || []).forEach((item) => {
      if (item && item.handle) {
        const inferred = inferMeta(item.handle);
        const coverage = productCoverageMap[item.handle] || {};
        const branchUses = (coverage.branches && coverage.branches.length)
          ? coverage.branches
          : (inferred.useCases || []);
        byHandle[item.handle] = {
          ...inferred,
          ...item,
          useCases: Array.from(new Set(branchUses)),
          materials: Array.from(new Set([...(coverage.materials || []), ...(inferred.materials || []), ...((item.materials || []))])),
          positions: Array.from(new Set([...(coverage.positions || []), ...(inferred.positions || []), ...((item.positions || []))])),
          features: Array.from(new Set([...(coverage.features || []), ...(inferred.features || []), ...((item.features || []))])),
          priority: coverage.priority || item.priority || inferred.priority,
          winPaths: coverage.winPaths || []
        };
      }
    });

    productData = Object.values(byHandle).filter(function (item) {
      return item && item.handle && item.handle !== 'organic-buckwheat-hull-pillow' && item.handle !== 'organic-buckwheat-hulls-knee-pillow' && item.handle !== 'wave-pillow' && item.handle !== 'beauty-pillow' && item.handle !== 'facial-surgery-recovery-pillow' && item.handle !== 'british-wool-contour-pillow-natural-anti-allergy-luxury';
    });
  }

  mergeCatalogData();
  const form = wrapper.querySelector('[data-quiz-form]');
  const results = wrapper.querySelector('[data-results]');
  const resultsGrid = wrapper.querySelector('[data-results-grid]');
  const progressBar = wrapper.querySelector('[data-progress-bar]');
  const progressFill = progressBar ? progressBar.querySelector('.pf-v2-progress-bar__fill') : null;
  const progressSteps = wrapper.querySelectorAll('[data-progress-step]');
  const backBtn = wrapper.querySelector('[data-action="back"]');
  const skipBtn = wrapper.querySelector('[data-action="skip"]');
  const restartBtn = wrapper.querySelector('[data-action="restart"]');
  const resultsSummaryEl = wrapper.querySelector('[data-results-summary]');
  const resultsSummaryText = wrapper.querySelector('[data-results-summary-text]');
  const resultsTitle = wrapper.querySelector('[data-results-title]');
  const resultsSubtitle = wrapper.querySelector('[data-results-subtitle]');
  const resultsWhyEl = wrapper.querySelector('[data-results-why]');
  const resultsWhyText = wrapper.querySelector('[data-results-why-text]');
  let currentStep = 1;
  let currentBranch = 'root';
  let stepHistory = [1];

  const branchKeys = ['neck', 'body', 'sitting', 'pregnancy', 'specialist'];
  const totalSteps = 3;
  let quizHasPreferenceStep = true;

  function setPreferenceStepVisible(visible) {
    quizHasPreferenceStep = visible;
    progressSteps.forEach((el) => {
      if (el.dataset.progressStep === '3') {
        el.hidden = !visible;
      }
    });
  }

  function inList(list, value) {
    return Array.isArray(list) && list.includes(value);
  }

  
  const neckStep3Config = {
    side: {
      question: 'Preferred material for side sleeping?',
      options: [
        { value: 'memory_foam', label: 'Firm & Contoured', desc: 'Memory foam designed to bridge the shoulder-to-neck gap' },
        { value: 'natural', label: 'Natural & Cooling', desc: 'Graphite-latex or British wool for breathable side support' },
        { value: 'soft', label: 'Soft & Adjustable', desc: 'Adjustable fibre fill or traditional soft pillow shape' },
        { value: 'any', label: 'No preference', desc: 'Show best overall options' }
      ]
    },
    back: {
      question: 'Preferred support for back sleeping?',
      options: [
        { value: 'memory_foam', label: 'Firm & Contoured', desc: 'Lower profile memory foam to keep the neck perfectly aligned' },
        { value: 'roll', label: 'Cervical Roll', desc: 'Targeted support under the neck curve only' },
        { value: 'natural', label: 'Natural & Adjustable', desc: 'Adjustable latex for customisable back support' },
        { value: 'any', label: 'No preference', desc: 'Show best overall options' }
      ]
    },
    mixed: {
      question: 'What matters most for mixed sleeping?',
      options: [
        { value: 'adjustable', label: 'Adjustable thickness', desc: 'Easily add or remove filling to find the perfect middle ground' },
        { value: 'travel', label: 'Compact / Travel', desc: 'Smaller versatile pillow that works well as you shift positions' },
        { value: 'natural', label: 'Natural materials', desc: 'Breathable fills like wool or latex' },
        { value: 'any', label: 'No preference', desc: 'Show best overall options' }
      ]
    }
  };

  const bodyStep3Config = {
    knee: {
      question: 'Which material or style do you prefer?',
      options: [
        { value: 'memory_foam', label: 'Memory foam with strap', desc: 'Contouring foam that stays in place all night' },
        { value: 'natural', label: 'British Wool', desc: 'Natural, breathable, and temperature regulating' },
        { value: 'any', label: 'No preference', desc: 'Show best overall options' }
      ]
    }
  };

  function preferenceIconHtml(opt) {
    if (opt && opt.img) {
      return {
        cls: 'pf-v2-option-icon pf-v2-option-icon--img',
        html: '<img src="' + String(opt.img).replace(/"/g, '') + '" alt="" width="46" height="46">'
      };
    }
    const svgs = {
      any: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
      memory_foam: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 8h16v4H4z"/><path d="M4 12h16v4H4z"/><path d="M7 8V6h10v2"/></svg>',
      natural: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21c0-7 5-10 9-10-2 6-6 8-9 10"/><path d="M12 21c0-7-5-10-9-10 2 6 6 8 9 10"/><path d="M12 21V9"/></svg>',
      roll: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><ellipse cx="12" cy="7" rx="7" ry="3"/><path d="M5 7v10c0 1.7 3.1 3 7 3s7-1.3 7-3V7"/></svg>',
      backrest: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M7 20V9c0-2 2-4 5-4s5 2 5 4v11"/><path d="M6 14h12"/></svg>',
      ring: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/></svg>',
      coccyx_wedge: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M4 18h16L8 6z"/></svg>',
      booster: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 19V7"/><path d="m7 12 5-5 5 5"/><path d="M5 19h14"/></svg>',
      wedge: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M3 19h18L6 5z"/></svg>',
      kneeling: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="5" r="2"/><path d="M9 22l2-8 3 2 3 6"/><path d="M8 12h5l2 3"/></svg>',
      home: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M4 11 12 4l8 7v9H4z"/><path d="M9 20v-6h6v6"/></svg>',
      travel: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><rect x="6" y="7" width="12" height="13" rx="2"/><path d="M9 7V5h6v2"/></svg>'
    };
    return {
      cls: opt.value === 'any' ? 'pf-v2-option-icon pf-v2-option-icon--nopref' : 'pf-v2-option-icon',
      html: svgs[opt.value] || svgs.any
    };
  }

  function renderStep3Options(optionsContainer, inputName, options) {
    optionsContainer.innerHTML = '';
    options.forEach((opt) => {
      const label = document.createElement('label');
      label.className = 'pf-v2-option';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = inputName;
      input.value = opt.value;
      label.appendChild(input);

      const content = document.createElement('span');
      content.className = 'pf-v2-option-content';

      const iconSpan = document.createElement('span');
      const icon = preferenceIconHtml(opt);
      iconSpan.className = icon.cls;
      iconSpan.innerHTML = icon.html;
      content.appendChild(iconSpan);

      const textSpan = document.createElement('span');
      textSpan.className = 'pf-v2-option-text';
      textSpan.innerHTML = `<strong>${opt.label}</strong><span>${opt.desc}</span>`;
      content.appendChild(textSpan);

      const arrow = document.createElement('span');
      arrow.className = 'pf-v2-option-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      content.appendChild(arrow);

      label.appendChild(content);
      optionsContainer.appendChild(label);
    });
  }

  function updateDynamicStep3(branch, value, configObj, questionKey, optionsKey, inputName) {
    const config = configObj[value];
    const questionEl = wrapper.querySelector(`[data-${branch}-question]`);
    const optionsContainer = wrapper.querySelector(`[data-${branch}-step3-options]`);
    if (!questionEl || !optionsContainer) return;
    if (!config) {
      optionsContainer.innerHTML = '';
      return;
    }

    questionEl.textContent = config.question;
    const answers = getFormData();
    const options = livePreferenceOptions(branch, answers);
    renderStep3Options(optionsContainer, inputName, options);
  }

  /* ── Dynamic sitting step 3: different options per sittingType ── */
  const sittingStep3Config = {
    lumbar: {
      question: 'What type of back support do you need?',
      options: [
        {
          value: 'roll',
          label: 'Lumbar roll',
          desc: 'Compact cylindrical roll that slots behind the lower back — easy to move between chairs, car, and sofa',
          img: finderImages.lumbar,
          alt: 'Lumbar support roll'
        },
        {
          value: 'backrest',
          label: 'Full backrest cushion',
          desc: 'Structured backrest panel that straps to your chair for deep, full-width lower back support',
          img: finderImages.restaback,
          alt: 'Full backrest cushion'
        },
        { value: 'any', label: 'No preference', desc: 'Show me the best back support options' }
      ]
    },
    seat: {
      question: 'What type of seat cushion suits you best?',
      options: [
        {
          value: 'ring',
          label: 'Ring / donut cushion',
          desc: 'Circular cushion with centre cut-out — removes all pressure from the tailbone and perineal area',
          img: finderImages.ring,
          alt: 'Ring donut seat cushion'
        },
        {
          value: 'coccyx_wedge',
          label: 'Coccyx wedge cushion',
          desc: 'Angled wedge with a coccyx cut-out — tilts pelvis forward while relieving tailbone pressure',
          img: finderImages.wedge8coccyx,
          alt: 'Coccyx wedge cushion'
        },
        {
          value: 'booster',
          label: 'Seat raiser / booster',
          desc: 'Flat firm cushion that adds height — ideal if your chair is too low or you need help standing up',
          img: finderImages.booster,
          alt: 'Seat height booster cushion'
        },
        { value: 'any', label: 'No preference', desc: 'Show me the best seat cushion options' }
      ]
    },
    posture: {
      question: 'How would you like to improve your posture?',
      options: [
        {
          value: 'wedge',
          label: 'Posture wedge cushion',
          desc: 'Angled seat pad that tilts your pelvis forward — naturally straightens the spine',
          img: finderImages.wedge8,
          alt: 'Posture wedge cushion'
        },
        {
          value: 'kneeling',
          label: 'Kneeling chair',
          desc: 'Ergonomic kneeling position that strengthens your core and trains upright posture over time',
          img: finderImages.kneeling,
          alt: 'Ergonomic kneeling chair'
        },
        { value: 'any', label: 'No preference', desc: 'Show me the best posture options' }
      ]
    }
  };

  const specialistStep3Config = {
    cpap: {
      question: 'Where will you use the CPAP pillow most?',
      options: [
        { value: 'home', label: 'At home', desc: 'Full-size contour pillow for nightly CPAP use' },
        { value: 'travel', label: 'Travel / compact', desc: 'Smaller pillow for trips and overnight stays' },
        { value: 'any', label: 'No preference', desc: 'Show the best overall CPAP options' }
      ]
    }
  };

  function step3FieldName(branch) {
    return {
      neck: 'material',
      body: 'bodyFeature',
      sitting: 'sittingFeature',
      specialist: 'specialistFeature'
    }[branch];
  }

  function getStep3ConfigForAnswers(branch, answers) {
    if (branch === 'neck') return neckStep3Config[answers.sleepPosition] || null;
    if (branch === 'body') return bodyStep3Config[answers.bodyArea] || null;
    if (branch === 'sitting') return sittingStep3Config[answers.sittingType] || null;
    if (branch === 'specialist') return specialistStep3Config[answers.specialistType] || null;
    return null;
  }

  function optionCoversProducts(branch, answers, optionValue) {
    const field = step3FieldName(branch);
    if (!field) return false;
    const hypo = Object.assign({}, answers, { [field]: optionValue });
    return getCandidates(hypo).some((product) => scoreProduct(product, hypo).score > 0);
  }

  function livePreferenceOptions(branch, answers) {
    const config = getStep3ConfigForAnswers(branch, answers);
    if (!config) return [];
    const live = config.options.filter((opt) => opt.value !== 'any' && optionCoversProducts(branch, answers, opt.value));
    if (live.length < 2) return [];
    const anyOpt = config.options.find((opt) => opt.value === 'any');
    return anyOpt ? live.concat(anyOpt) : live;
  }

  function shouldShowPreferenceStep(branch, answers) {
    /* Posture step 3 is a product picker (wedge vs kneeling chair).
       Asking that, then showing the same chair in a 3-card grid, is a wasted
       click — step 2 already said “wedge cushions, kneeling chairs”. */
    if (branch === 'sitting' && answers.sittingType === 'posture') return false;
    return livePreferenceOptions(branch, answers).length >= 2;
  }

  function updateSittingStep3(sittingType) {
    const config = sittingStep3Config[sittingType];
    const questionEl = wrapper.querySelector('[data-sitting-question]');
    const optionsContainer = wrapper.querySelector('[data-sitting-step3-options]');
    if (!questionEl || !optionsContainer) return;
    if (!config) {
      optionsContainer.innerHTML = '';
      return;
    }

    questionEl.textContent = config.question;
    const answers = getFormData();
    renderStep3Options(optionsContainer, 'sittingFeature', livePreferenceOptions('sitting', answers));
  }

  function getStepElement(stepNum, branch) {
    if (stepNum === 1) {
      return wrapper.querySelector('[data-step="1"][data-branch="root"]');
    }
    return wrapper.querySelector(`[data-step="${stepNum}"][data-branch-path="${branch}"]`);
  }

  function updateProgressSteps(activeStep) {
    progressSteps.forEach((el) => {
      const stepVal = el.dataset.progressStep;
      el.classList.remove('pf-v2-progress-step--active', 'pf-v2-progress-step--done');
      if (stepVal === 'done') return;
      const stepNum = parseInt(stepVal, 10);
      if (stepNum < activeStep) {
        el.classList.add('pf-v2-progress-step--done');
      } else if (stepNum === activeStep) {
        el.classList.add('pf-v2-progress-step--active');
      }
    });
    // Update fill bar
    const stepsCount = quizHasPreferenceStep ? 3 : 2;
    const pct = Math.max(8, ((activeStep - 1) / stepsCount) * 100 + 12);
    if (progressFill) {
      progressFill.style.width = pct + '%';
    } else if (progressBar) {
      progressBar.style.setProperty('--pf-progress-width', pct + '%');
    }
  }

  function scrollToQuiz() {
    // Smooth scroll to quiz wrapper top, fixing the mobile scroll issue
    const rect = wrapper.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - 20;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }

  /**
   * Dynamically replace step 3 card images with the actual #1 product image
   * for each radio option. This way users see a real product photo that matches
   * what they'll get if they choose that option.
   */
  function updateStep3CardImages(stepElement) {
    const answers = getFormData();
    const candidates = getCandidates(answers);
    const radioOptions = stepElement.querySelectorAll('input[type="radio"]');

    radioOptions.forEach((radio) => {
      const optionName = radio.name;
      const optionValue = radio.value;
      if (optionValue === 'any') return; // skip "No preference"

      // Build a hypothetical answer set with this option selected
      const hypothetical = { ...answers, [optionName]: optionValue };

      // Score all candidates against this hypothetical
      const scored = candidates
        .map(p => ({ ...p, score: scoreProduct(p, hypothetical).score }))
        .filter(p => p.score > 0)
        .sort((a, b) => b.score - a.score);

      const top = scored[0];
      if (!top || !top.handle) return;

      const imgContainer = radio.closest('.pf-v2-option')?.querySelector('.pf-v2-option-icon--img img');
      if (!imgContainer) return;

      // Fetch the real product image from Shopify
      fetchWithTimeout(`${storeRoot}products/${encodeURIComponent(top.handle)}.js`, { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : null)
        .then(json => {
          if (!json || !json.featured_image) return;
          const src = json.featured_image.includes('?')
            ? json.featured_image + '&width=400'
            : json.featured_image + '?width=400';
          imgContainer.src = src;
          imgContainer.alt = json.title || top.handle;
        })
        .catch(() => {});
    });
  }

  function showStep(stepNum, direction) {
    const oldStep = wrapper.querySelector('.pf-v2-step--active');
    const boundedStep = Math.max(1, Math.min(stepNum, totalSteps));
    const stepElement = getStepElement(boundedStep, currentBranch);

    /* ── Dynamic step 3: same card layout as step 1, no product photos ── */
    if (boundedStep === 3 && stepElement) {
      /* photos removed — mixed images made the grid uneven */
    }

    // Exit animation on current step
    if (oldStep && oldStep !== stepElement) {
      oldStep.classList.add('pf-v2-step--exiting');
      oldStep.classList.remove('pf-v2-step--active');
      setTimeout(() => {
        oldStep.classList.remove('pf-v2-step--exiting');
      }, 250);
    } else if (oldStep) {
      oldStep.classList.remove('pf-v2-step--active');
    }

    // Enter animation on new step
    if (stepElement) {
      setTimeout(() => {
        stepElement.classList.add('pf-v2-step--active', 'pf-v2-step--entering');
        setTimeout(() => {
          stepElement.classList.remove('pf-v2-step--entering');
        }, 400);
      }, oldStep && oldStep !== stepElement ? 150 : 0);
    }

    updateProgressSteps(boundedStep);
    backBtn.hidden = boundedStep === 1;
    backBtn.disabled = boundedStep === 1;
    currentStep = boundedStep;

    // Scroll to top of quiz on mobile after step change
    if (window.innerWidth < 990 || boundedStep > 1) {
      setTimeout(scrollToQuiz, 180);
    }
  }
  
  function getFormData() {
    const formData = new FormData(form);
    return {
      primaryNeed: formData.get('primaryNeed'),
      sleepPosition: formData.get('sleepPosition'),
      material: formData.get('material'),
      bodyArea: formData.get('bodyArea'),
      bodyFeature: formData.get('bodyFeature'),
      neckFeature: formData.get('material'),
      sittingType: formData.get('sittingType'),
      sittingFeature: formData.get('sittingFeature'),
      pregnancyType: formData.get('pregnancyType'),
      pregnancyFeature: formData.get('pregnancyFeature'),
      specialistType: formData.get('specialistType'),
      specialistFeature: formData.get('specialistFeature')
    };
  }
  
  function scoreProduct(product, answers) {
    let score = product.priority || 0;
    const reasons = [];

    /* Skip-to-popular: no answers yet. Rank by catalogue priority only.
       The -50 “wrong branch” penalty used to push every product below 0
       and the results grid rendered empty. */
    if (!answers || !answers.primaryNeed) {
      return { score, reasons };
    }
    
    const addReason = (text) => {
      if (text && !reasons.includes(text)) reasons.push(text);
    };
    
    const useCases = product.useCases || [];
    const materials = product.materials || [];
    const positions = product.positions || [];
    const features = product.features || [];
    const winPaths = product.winPaths || [];
    const handleText = (product.handle || '').toLowerCase();
    const titleText = (product.title || '').toLowerCase();
    const productText = `${handleText} ${titleText}`;

    if (inList(useCases, answers.primaryNeed)) {
      score += 30;
    } else {
      score -= 50;
    }

    if (winPaths.some((path) => matchesWinPath(path, answers))) {
      score += 45;
    }

    switch (answers.primaryNeed) {
      case 'neck':
        if (answers.sleepPosition && inList(positions, answers.sleepPosition)) {
          score += 15;
          const posWord = { side: 'side', back: 'back', front: 'front', mixed: 'combination' }[answers.sleepPosition] || answers.sleepPosition;
          addReason(`Height profile suits ${posWord} sleeping`);
        }
        
        const reqMaterial = answers.material; // the neckFeature
        
        if (reqMaterial === 'memory_foam' && inList(materials, 'memory_foam')) {
          score += 25;
          addReason('Firm memory foam support');
        } else if (reqMaterial === 'natural' && inList(materials, 'natural')) {
          score += 25;
          addReason('Natural & cooling materials');
        } else if (reqMaterial === 'soft' && (inList(materials, 'soft') || inList(materials, 'adjustable'))) {
          score += 25;
          addReason('Soft adjustable fill');
        } else if (reqMaterial === 'roll' && productText.includes('roll')) {
          score += 35;
          addReason('Targeted cervical roll support');
        } else if (reqMaterial === 'adjustable' && inList(features, 'height_adjustable')) {
          score += 25;
          addReason('Fully adjustable height');
        } else if (reqMaterial === 'travel' && inList(features, 'travel')) {
          score += 30;
          addReason('Compact travel profile');
        }

        /* Penalise non-neck products */
        if (productText.includes('specialist') && !productText.includes('neck')) score -= 10;
        if (productText.includes('leg') || productText.includes('wedge') || productText.includes('knee')) score -= 20;
        break;
      case 'body':
        /* Body logic: knee / legs / upper_body */
        if (answers.bodyArea === 'knee') {

          if (inList(features, 'knee_alignment') || productText.includes('knee')) {
            score += 25;
            addReason('Built for knee alignment');
          }
          if (answers.bodyFeature === 'memory_foam' && inList(materials, 'memory_foam')) {
            score += 20;
            addReason('Memory foam with strap');
          } else if (answers.bodyFeature === 'natural' && inList(materials, 'natural') && productText.includes('wool')) {
            score += 20;
            addReason('Natural British wool');
          }
          /* Penalise non-knee products */
          if (!productText.includes('knee') && !inList(features, 'knee_alignment')) score -= 30;
        } 
        else if (answers.bodyArea === 'body') {
          if (productText.includes('leg-rest') || productText.includes('leg rest')) {
            score += 40;
            addReason('Deep leg elevation for circulation');
          } else if (productText.includes('heel') || productText.includes('heel-lift')) {
            score += 40;
            addReason('Targeted heel and lower leg relief');
          } else if (productText.includes('t-roll') || productText.includes('positioning')) {
            score += 25;
            addReason('Leg positioning support');
          } else if (handleText === 'orthopaedic-hip-abduction-wedge-velcro-straps') {
            score += 18;
            addReason('Hip abduction wedge for post-surgery positioning');
          } else {
            score -= 40;
          }
        }
        else if (answers.bodyArea === 'upper_body') {
          if (productText.includes('bed-wedge') || (productText.includes('wedge') && !inList(features, 'seat_relief') && !productText.includes('coccyx'))) {
            score += 40;
            addReason('Upper body elevation');
            if (inList(features, 'travel') || productText.includes('travel')) {
              addReason('Available in a travel-friendly version');
            }
          } else {
            score -= 40;
          }
        }
        break;

      case 'sitting':
        {
          /* ── Step 2: broad category ── */
          const isLumbarProduct = inList(features, 'lumbar') || productText.includes('lumbar') || productText.includes('backrest') || productText.includes('rest-a-back');
          const isSeatProduct = inList(features, 'seat_relief') || productText.includes('ring') || productText.includes('coccyx') || productText.includes('bariatric') || productText.includes('bariactric') || productText.includes('comfort-cushion') || productText.includes('seat height');
          const isPostureProduct = (inList(features, 'posture') || productText.includes('kneeling')) && !isSeatProduct;

          if (answers.sittingType === 'lumbar') {
            if (isLumbarProduct) score += 25;
            if (isSeatProduct && !isLumbarProduct) score -= 30;
            if (isPostureProduct && !isLumbarProduct) score -= 20;
          }
          if (answers.sittingType === 'seat') {
            if (isSeatProduct) score += 25;
            if (isLumbarProduct && !isSeatProduct) score -= 30;
            if (isPostureProduct && !isSeatProduct) score -= 20;
          }
          if (answers.sittingType === 'posture') {
            if (isPostureProduct) score += 25;
            if (isSeatProduct && !isPostureProduct) score -= 20;
            if (isLumbarProduct && !isPostureProduct) score -= 20;
          }

          /* ── Step 3: specific product type ── */
          /* Lumbar sub-types */
          if (answers.sittingFeature === 'roll') {
            if (productText.includes('lumbar-support') || productText.includes('lumbar roll') || productText.includes('compact-backrest')) {
              score += 30; addReason('Compact lumbar roll — easy to move between chairs');
            } else if (isLumbarProduct) {
              score -= 10;
            }
          }
          if (answers.sittingFeature === 'backrest') {
            if (productText.includes('rest-a-back') || productText.includes('large-backrest') || productText.includes('superest')) {
              score += 30; addReason('Full structured backrest support');
            } else if (isLumbarProduct) {
              score -= 10;
            }
          }

          /* Seat sub-types */
          if (answers.sittingFeature === 'ring') {
            if (productText.includes('ring') || productText.includes('donut') || productText.includes('sero-pressure') || productText.includes('bariactric') || productText.includes('bariatric')) {
              score += 30; addReason('Ring/donut cushion for pressure relief');
            } else if (isSeatProduct) {
              score -= 15;
            }
          }
          if (answers.sittingFeature === 'coccyx_wedge') {
            if (productText.includes('coccyx') || (productText.includes('wedge') && inList(features, 'seat_relief'))) {
              score += 30; addReason('Coccyx cut-out wedge for tailbone relief');
            } else if (isSeatProduct) {
              score -= 15;
            }
          }
          if (answers.sittingFeature === 'booster') {
            if (productText.includes('comfort-cushion') || productText.includes('seat height') || productText.includes('booster')) {
              score += 30; addReason('Seat height booster cushion');
            } else if (isSeatProduct) {
              score -= 15;
            }
          }

          /* Posture sub-types */
          if (answers.sittingFeature === 'wedge') {
            if ((productText.includes('8-wedge') || productText.includes('putnam-wedge') || productText.includes('11° sitting') || productText.includes('seat wedge')) && !productText.includes('coccyx')) {
              score += 30; addReason('Posture wedge tilts pelvis for natural alignment');
            } else if (handleText === 'rest-a-head') {
              score += 14; addReason('Portable head and posture support');
            } else if (isPostureProduct) {
              score -= 10;
            }
          }
          if (answers.sittingFeature === 'kneeling') {
            if (productText.includes('kneeling')) {
              score += 35; addReason('Ergonomic kneeling chair for active posture');
            } else {
              score -= 20;
            }
          }

          /* Superest is a hybrid — boost it slightly for lumbar→any and seat→any */
          if (productText.includes('superest')) {
            if (answers.sittingType === 'lumbar' && answers.sittingFeature === 'any') score += 8;
            if (answers.sittingType === 'seat' && answers.sittingFeature === 'any') score += 5;
          }
          if (handleText.includes('postpartum')) score -= 12;
        }
        break;
      case 'pregnancy':
        /* Sleep & bed path: favour full-body pillows and labour cushion */
        if (answers.pregnancyType === 'sleep') {
          if (handleText === 'u-shaped-pregnancy-pillow') {
            score += 40;
            addReason('Full-body U-shape for bump, back and knees');
          } else if (handleText === 'pregnancy-cuddle-pillow') {
            score += 24;
            addReason('Cuddle pillow for bump and side-sleeping comfort');
          } else if (handleText === 'pregnancy-labour-birthing-cushion') {
            score += 12;
            addReason('Labour and positioning cushion');
          } else if (inList(features, 'full_body')) {
            score += 18;
            addReason('Full-body pregnancy support');
          }
          if (inList(features, 'seat_relief') && !inList(features, 'full_body')) {
            score -= 15;
          }
        }
        /* Sitting & recovery path: favour ring cushion, then full-body as secondary */
        if (answers.pregnancyType === 'sitting_recovery') {
          if (inList(features, 'seat_relief') || productText.includes('postpartum') || productText.includes('ring')) {
            score += 25;
            addReason('Sitting comfort during pregnancy & recovery');
          }
          if (inList(features, 'full_body') || productText.includes('total-body') || productText.includes('u-shaped')) {
            score += 10;
            addReason('Versatile full-body support');
          }
        }
        /* General pregnancy relevance */
        if (inList(positions, 'side')) {
          score += 5;
          addReason('Good for side sleeping');
        }
        break;
      case 'specialist':
        if (answers.specialistType === 'cpap') {
          if (handleText === 'advanced-cpap-pillow') {
            score += 80;
            addReason('Best-selling contour CPAP pillow for nightly mask comfort');
          } else if (handleText === 'memory-foam-advanced-cpap-pillow') {
            score += 70;
            addReason('Memory foam CPAP contour for firmer mask support');
          } else if (handleText === 'original-cpap-pillow') {
            score += 55;
            addReason('Fibre-filled CPAP pillow with mask cut-outs');
          } else if (handleText === 'travel-cpap-pillow') {
            score += 45;
            addReason('Compact CPAP pillow for overnight trips');
          } else if (productText.includes('cpap')) {
            score += 30;
            addReason('Purpose-built to accommodate CPAP masks without leaks');
          } else {
            score -= 40;
          }
          if (answers.specialistFeature === 'travel') {
            if (handleText === 'travel-cpap-pillow') score += 40;
            else if (productText.includes('cpap')) score -= 8;
          } else if (answers.specialistFeature === 'home') {
            if (handleText === 'travel-cpap-pillow') score -= 20;
          }
        }
        else if (answers.specialistType === 'stomach_sleeper') {
          if (handleText === 'face-pillow' || handleText === 'butterfly-pillow') {
            score += 80;
            addReason('Designed for front/stomach sleepers to reduce neck strain');
          } else if (productText.includes('face') || productText.includes('stomach')) {
            score += 40;
            addReason('Supports stomach and front sleeping position');
          } else if (productText.includes('low-profile') || productText.includes('low profile')) {
            score += 30;
            addReason('Low-profile design suits stomach sleepers');
          } else {
            score -= 40;
          }
        }
        else if (answers.specialistType === 'hip_surgery') {
          if (handleText === 'orthopaedic-hip-abduction-wedge-velcro-straps') {
            score += 80;
            addReason('Orthopaedic hip abduction wedge for post-surgery positioning');
          } else if (handleText === 't-roll-positioning-aid') {
            score += 70;
            addReason('Versatile T-roll for post-surgery body positioning');
          } else if (handleText === 'heel-lift-pillow') {
            score += 65;
            addReason('Heel & lower leg elevation for recovery and circulation');
          } else if (handleText === 'leg-rest') {
            score += 60;
            addReason('Leg elevation supports post-surgery circulation & swelling');
          } else if (handleText === 'lumbar-support-d-shape-bed-pillow') {
            score += 50;
            addReason('D-shape pillow aids comfortable positioning in bed');
          } else {
            score -= 40;
          }
        }
        else if (answers.specialistType === 'reflux') {
          if (handleText === 'bed-wedge') {
            score += 80;
            addReason('Angled wedge elevates the upper body for reflux relief');
          } else if (handleText === 'memory-foam-travel-bed-wedge') {
            score += 70;
            addReason('Portable wedge for reflux and elevated sleep on the move');
          } else {
            score -= 40;
          }
        }
        break;
    }

    return { score, reasons: reasons.slice(0, 4) };
  }
  
  function getCandidates(answers) {
    const branch = answers.primaryNeed;
    const catalog = productData.filter((product) => product && product.handle && !product._missing);
    const branchesOf = (product) => (productCoverageMap[product.handle] || {}).branches || product.useCases || [];
    const inBranch = (product) => inList(branchesOf(product), branch) || inList(product.useCases, branch);

    if (!branch) {
      const popular = [
        'u-shaped-pregnancy-pillow',
        'knee-pillow',
        'heel-lift-pillow',
        'rest-a-head',
        '8-wedge',
        'bed-wedge'
      ];
      const picked = popular
        .map((handle) => catalog.find((product) => product.handle === handle))
        .filter(Boolean);
      const rest = catalog
        .filter((product) => !popular.includes(product.handle) && !String(product.handle).includes('cpap'))
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
      return picked.concat(rest);
    }

    let merged = catalog.filter(inBranch);

    if (branch === 'neck') {
      if (answers.sleepPosition === 'front') {
        merged = catalog.filter((product) => product.handle === 'face-pillow' || product.handle === 'butterfly-pillow');
      } else {
        merged = catalog.filter((product) =>
          inList(branchesOf(product), 'neck') &&
          product.handle !== 'face-pillow' &&
          product.handle !== 'butterfly-pillow' &&
          !product.handle.includes('cpap')
        );
      }
    } else if (branch === 'body') {
      if (answers.bodyArea === 'knee') {
        merged = catalog.filter((product) => product.handle.includes('knee') && !product.handle.includes('kneeling'));
      } else if (answers.bodyArea === 'body') {
        merged = catalog.filter((product) => [
          'leg-rest',
          'heel-lift-pillow',
          't-roll-positioning-aid',
          'orthopaedic-hip-abduction-wedge-velcro-straps'
        ].includes(product.handle));
      } else if (answers.bodyArea === 'upper_body') {
        merged = catalog.filter((product) =>
          product.handle === 'bed-wedge' || product.handle === 'memory-foam-travel-bed-wedge'
        );
      } else {
        merged = catalog.filter((product) => inList(branchesOf(product), 'body'));
      }
    } else if (branch === 'sitting') {
      merged = catalog.filter((product) => inList(branchesOf(product), 'sitting'));
      if (answers.sittingType === 'posture') {
        merged = merged.filter((product) => {
          const handle = product.handle || '';
          return handle.includes('kneeling') || handle === '8-wedge' || handle === 'putnam-wedge' || handle === 'rest-a-head';
        });
      }
    } else if (branch === 'pregnancy') {
      merged = catalog.filter((product) => inList(branchesOf(product), 'pregnancy'));
    } else if (branch === 'specialist') {
      if (answers.specialistType === 'cpap') {
        merged = catalog.filter((product) => product.handle.includes('cpap'));
      } else if (answers.specialistType === 'stomach_sleeper') {
        merged = catalog.filter((product) => product.handle === 'face-pillow' || product.handle === 'butterfly-pillow');
      } else if (answers.specialistType === 'hip_surgery') {
        merged = catalog.filter((product) => [
          'orthopaedic-hip-abduction-wedge-velcro-straps',
          't-roll-positioning-aid',
          'heel-lift-pillow',
          'leg-rest',
          'lumbar-support-d-shape-bed-pillow'
        ].includes(product.handle));
      } else if (answers.specialistType === 'reflux') {
        merged = catalog.filter((product) =>
          product.handle === 'bed-wedge' || product.handle === 'memory-foam-travel-bed-wedge'
        );
      } else {
        merged = catalog.filter((product) => inList(branchesOf(product), 'specialist'));
      }
    }

    return merged;
  }

  /* ── Personalisation helpers ── */
  const needLabels = {
    neck: 'Neck & Sleep',
    body: 'Leg & Body Support',
    elevated: 'Elevated Support',
    sitting: 'Sitting & Posture',
    pregnancy: 'Pregnancy',
    specialist: 'Specialist & Medical',
    cpap: 'CPAP & Sleep Apnoea',
    stomach_sleeper: 'Stomach / Front Sleeper',
    hip_surgery: 'Post-Surgery & Orthopaedic Recovery',
    reflux: 'Acid Reflux & Elevated Sleeping'
  };

  function getBadgeLabel(index) {
    if (index === 0) return { text: 'Best match', cls: 'bestseller' };
    if (index === 1) return { text: 'Popular choice', cls: 'popular' };
    return { text: 'Top pick', cls: 'top' };
  }

  function getResultsHeading(answers) {
    const need = answers.primaryNeed;
    /* Reached via the skip link with nothing answered — say so plainly
       rather than claiming these are "personalised". */
    if (!need) {
      return {
        title: 'Our most popular support pillows',
        subtitle: 'A good place to start. Retake the quiz any time for a recommendation matched to you.'
      };
    }
    const label = needLabels[need] || 'Your Needs';
    return {
      title: `Your personalised ${label.toLowerCase()} recommendations`,
      subtitle: `Based on your answers, these pillows are the best fit for you.`
    };
  }

  function getSummaryText(answers) {
    const parts = [];
    const need = answers.primaryNeed;
    /* Skip link path: nothing was answered. Guard before .toLowerCase(),
       which throws a TypeError on undefined and took the whole results
       render down with it. */
    if (!need) {
      return 'These are our best selling support pillows overall. Answer the three questions for a recommendation matched to how you sleep.';
    }
    parts.push(`You told us your main concern is <strong>${(needLabels[need] || need).toLowerCase()}</strong>`);
    if (need === 'neck' && answers.sleepPosition) {
      const posLabel = { side: 'side sleeper', back: 'back sleeper', front: 'front sleeper', mixed: 'combination sleeper' };
      parts.push(`you sleep as a <strong>${posLabel[answers.sleepPosition] || answers.sleepPosition}</strong>`);
    }
    if (need === 'pregnancy' && answers.pregnancyType) {
      parts.push(`you need <strong>${answers.pregnancyType.replace(/-/g, ' ')}</strong> support`);
    }
    if (need === 'sitting' && answers.sittingType) {
      parts.push(`for <strong>${answers.sittingType.replace(/-/g, ' ')}</strong>`);
    }
    return parts.join(', ') + '.';
  }

  function getWhyRecommended(answers, products) {
    const lines = [];
    const need = answers.primaryNeed;
    if (!need) {
      return 'Ranked by how often they are chosen across all our support pillows. Retake the quiz for a recommendation matched to your own answers.';
    }
    lines.push(`We matched products from our ${needLabels[need] || ''} range to your answers.`);
    if (products[0] && products[0].reasons && products[0].reasons.length) {
      lines.push(`Your top match scored highest because: ${products[0].reasons.slice(0, 2).join(' and ').toLowerCase()}.`);
    }
    lines.push('Each recommendation is ranked by how closely it fits your specific requirements.');
    return lines.join(' ');
  }

  function formatPrice(pence) {
    const value = Number(pence || 0) / 100;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  }

  async function enrichProduct(product) {
    if (product._enriched) return product;
    try {
      const res = await fetchWithTimeout(`${storeRoot}products/${encodeURIComponent(product.handle)}.js`);
      if (!res.ok) throw new Error('missing');
      const data = await res.json();
      if (!data || !data.id) throw new Error('missing');
      product.title = data.title || product.title;
      product.url = data.url || `/products/${product.handle}`;
      product.image = data.featured_image || '';
      product.price = data.price ? formatPrice(data.price) : '';
      product.available = Boolean(data.available);
      product.summary = product.summary || (data.description ? data.description.replace(/<[^>]+>/g, '').slice(0, 120) + '…' : '');
      product.variants = (data.variants || []).map(v => ({
        id: v.id,
        title: v.title,
        label: v.title,
        available: v.available,
        price: formatPrice(v.price)
      }));
      product.variantOptions = data.options_with_values
        ? data.options_with_values.map(o => `${o.name}: ${o.values.join(', ')}`)
        : [];
      if (!product.image || !product.variants.some((variant) => variant.available)) {
        product._missing = true;
      }
    } catch (e) {
      product._missing = true;
      product.image = '';
      product.url = '';
      product.price = '';
      product.variants = [];
    }
    product._enriched = true;
    return product;
  }

  function renderStars(rating) {
    const r = parseFloat(rating) || 4.5;
    const full = Math.floor(r);
    const half = r - full >= 0.25 ? 1 : 0;
    const empty = 5 - full - half;
    let s = '';
    for (let i = 0; i < full; i++) s += '★';
    if (half) s += '½';
    for (let i = 0; i < empty; i++) s += '☆';
    return `<span class="pf-v2-result-stars" aria-label="${r} out of 5 stars">${s} <small>${r}</small></span>`;
  }

  function isGenericReason(text) {
    const t = String(text || '').trim();
    if (!t) return true;
    if (/^built for /i.test(t)) return true;
    if (/^direct match for this exact selection$/i.test(t)) return true;
    if (/^strong overall match$/i.test(t)) return true;
    if (/^(back support|seat cushion|posture improvement) product$/i.test(t)) return true;
    return false;
  }

  function reasonsForCards(products) {
    const cleaned = products.map((product) => (product.reasons || []).filter((reason) => !isGenericReason(reason)));
    if (products.length <= 1) return cleaned.map((list) => list.slice(0, 2));
    const counts = {};
    cleaned.forEach((list) => {
      list.forEach((reason) => { counts[reason] = (counts[reason] || 0) + 1; });
    });
    return cleaned.map((list) => list.filter((reason) => counts[reason] === 1).slice(0, 2));
  }

  async function showResults() {
    const answers = getFormData();
    const candidates = getCandidates(answers);
    form.style.display = 'none';
    results.style.display = 'block';
    results.setAttribute('aria-busy', 'true');
    resultsGrid.innerHTML = '<p class="pf-v2-results-loading" role="status">Comparing the best available matches for you…</p>';
    results.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const skipPath = !answers.primaryNeed;
    const rankedAll = skipPath
      ? candidates.map((product) => ({ ...product, score: product.priority || 0, reasons: [] }))
      : candidates
          .map((product) => {
            const result = scoreProduct(product, answers);
            return { ...product, score: result.score, reasons: result.reasons };
          })
          .sort((a, b) => b.score - a.score);

    let ranked = skipPath
      ? rankedAll.slice(0, 24)
      : rankedAll.filter((item) => item.score > 0).slice(0, 12);

    if (!ranked.length) {
      ranked = rankedAll.filter((item) => item.score >= 0).slice(0, 12);
    }
    if (!ranked.length) {
      ranked = rankedAll.slice(0, 12);
    }
    if (answers.sittingFeature && answers.sittingFeature !== 'any' && ranked.length > 2) {
      ranked = ranked.slice(0, 2);
    }

    const resultCap = skipPath ? 6 : 3;
    const live = [];
    const pool = skipPath ? rankedAll : ranked;
    for (let i = 0; i < pool.length && live.length < resultCap; i++) {
      const seed = pool[i];
      if (!seed || live.some((item) => item.handle === seed.handle)) continue;
      const product = await enrichProduct(seed);
      if (product._missing || !product.image || !product.url) continue;
      live.push(product);
    }
    const enriched = live.map((product) => {
      const selectedVariant = Array.isArray(product.variants)
        ? product.variants.find((variant) => variant.available) || product.variants[0]
        : null;
      return { ...product, selectedVariantId: selectedVariant ? selectedVariant.id : '' };
    });

    if (!enriched.length) {
      resultsGrid.innerHTML = '<p class="pf-v2-results-empty">We could not match a live product to those answers. <a href="/collections/pillow-cushion">Browse support pillows</a>.</p>';
      results.removeAttribute('aria-busy');
      return;
    }

    /* ── Personalise heading & summary ── */
    const heading = getResultsHeading(answers);
    if (resultsTitle) resultsTitle.textContent = heading.title;
    if (resultsSubtitle) resultsSubtitle.textContent = heading.subtitle;

    if (resultsSummaryEl) resultsSummaryEl.style.display = 'none';
    if (resultsWhyEl) resultsWhyEl.style.display = 'none';

    /* ── Update progress to "done" state ── */
    progressSteps.forEach((el) => {
      el.classList.remove('pf-v2-progress-step--active');
      el.classList.add('pf-v2-progress-step--done');
    });
    if (progressFill) progressFill.style.width = '100%';

    /* ── Build result cards ── */
    const uniqueReasons = reasonsForCards(enriched);
    resultsGrid.setAttribute('data-count', String(enriched.length));
    resultsGrid.innerHTML = enriched.map((product, index) => {
      const badge = getBadgeLabel(index);
      const productUrl = safeUrl(product.url, '#');
      const productTitle = escapeHtml(product.title);
      const productImage = safeUrl(product.image, '');
      const productPrice = escapeHtml(product.price || '');
      const productSummary = escapeHtml(product.summary);
      const productReasons = uniqueReasons[index] || [];
      /* Real Loox data only. No reviews, no line — never a default rating. */
      const socialLine = (product.rating > 0 && product.ratingCount > 0)
        ? `<div class="pf-v2-result-social">${renderStars(product.rating)} ${Number(product.ratingCount)} review${product.ratingCount === 1 ? '' : 's'}</div>`
        : '';

      return `
      <article class="pf-v2-result-card" style="animation-delay:${index * 120}ms">
        <a href="${productUrl}" class="pf-v2-result-image">
          <span class="pf-v2-result-image-badge pf-v2-result-image-badge--${badge.cls}">${badge.text}</span>
          ${productImage
            ? `<img src="${productImage}${productImage.includes('?') ? '&amp;width=480' : '?width=480'}" alt="${productTitle}" loading="eager" width="240" height="240">`
            : `<span class="pf-v2-result-image-placeholder">No image</span>`
          }
        </a>
        <div class="pf-v2-result-content">
          <span class="pf-v2-result-badge">${badge.text}</span>
          ${socialLine}
          ${(product.handle && String(product.handle).toLowerCase().indexOf('putnam') !== -1) ? `<div class="pf-v2-result-uk-badge"><svg class="pf-v2-uk-flag" viewBox="0 0 60 30" width="18" height="10" aria-hidden="true"><rect width="60" height="30" fill="#012169"/><path stroke="#fff" stroke-width="6" d="M0 0l60 30M60 0L0 30"/><path stroke="#C8102E" stroke-width="2" d="M0 0l60 30M60 0L0 30"/><path stroke="#fff" stroke-width="10" d="M30 0v30M0 15h60"/><path stroke="#C8102E" stroke-width="6" d="M30 0v30M0 15h60"/></svg> Handmade in UK</div>` : ''}
          ${(product.handle && product.handle.indexOf('cpap') !== -1) ? '<div class="pf-v2-result-mask-notice"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg> CPAP mask shown for illustration only – mask not included</div>' : ''}
          <h3 class="pf-v2-result-title">
            <a href="${productUrl}">${productTitle}</a>
          </h3>
          ${productPrice ? `<div class="pf-v2-result-price">${productPrice}</div>` : ''}
          ${productSummary ? `<p class="pf-v2-result-summary">${productSummary}</p>` : ''}
          ${productReasons.length
            ? `<ul class="pf-v2-result-reasons">${productReasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul>`
            : ''}
          ${Array.isArray(product.variantOptions) && product.variantOptions.length
            ? `<p class="pf-v2-result-variants">${escapeHtml(product.variantOptions[0])}</p>`
            : ''
          }
          <div class="pf-v2-result-footer">
            <div class="pf-v2-atc">
              ${Array.isArray(product.variants) && product.variants.length > 1
                ? `<label class="pf-v2-variant-label">Choose option
                    <select class="pf-v2-variant-select" data-variant-select>
                      ${product.variants.map((variant) =>
                        `<option value="${Number(variant.id)}" ${variant.id === product.selectedVariantId ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.label || variant.title)}${variant.available ? '' : ' - Sold out'}</option>`
                      ).join('')}
                    </select>
                  </label>`
                : ''
              }
            </div>
            <div class="pf-v2-result-actions">
              ${product.selectedVariantId
                ? `<button
                    type="button"
                    class="button button--primary pf-v2-add-btn"
                    data-action="add-to-cart"
                    data-variant-id="${product.selectedVariantId}"
                  >Add to bag</button>`
                : ''
              }
              <a href="${productUrl}" class="button button--secondary pf-v2-view-btn">View details</a>
            </div>
          </div>
          <p class="pf-v2-atc-status" data-atc-status aria-live="polite"></p>
        </div>
      </article>`;
    }).join('');
    
    results.removeAttribute('aria-busy');
    track('finder_complete', {
      branch: currentBranch,
      result_count: enriched.length
    });
  }
  
  function restart() {
    track('finder_restart', { previous_branch: currentBranch });
    form.reset();
    currentStep = 1;
    currentBranch = 'root';
    stepHistory = [1];
    setPreferenceStepVisible(true);
    
    form.style.display = 'block';
    results.style.display = 'none';

    /* Reset progress steps */
    progressSteps.forEach((el) => {
      el.classList.remove('pf-v2-progress-step--active', 'pf-v2-progress-step--done');
    });
    if (resultsSummaryEl) resultsSummaryEl.style.display = 'none';
    if (resultsWhyEl) resultsWhyEl.style.display = 'none';
    
    showStep(1);
  }

  function clearBranchStepAnswers() {
    const names = ['sleepPosition', 'material', 'bodyArea', 'bodyFeature', 'sittingType', 'sittingFeature', 'pregnancyType', 'specialistType', 'specialistFeature'];
    names.forEach((name) => {
      wrapper.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
        input.checked = false;
      });
    });
  }
  
  // Robust option selection with debounce to prevent double-firing
  let lastOptionTime = 0;

  function handleRadioSelection(input) {
    const now = Date.now();
    if (now - lastOptionTime < 250) return;
    lastOptionTime = now;

    if (input.name === 'primaryNeed') {
      currentBranch = input.dataset.branch || 'neck';
      if (!branchKeys.includes(currentBranch)) {
        currentBranch = 'neck';
      }
      clearBranchStepAnswers();
      stepHistory = [1];
      setPreferenceStepVisible(true);
    }

    if (input.name === 'sleepPosition') {
      updateDynamicStep3('neck', input.value, neckStep3Config, 'neck-question', 'neck-step3-options', 'material');
    }
    if (input.name === 'bodyArea') {
      updateDynamicStep3('body', input.value, bodyStep3Config, 'body-question', 'body-step3-options', 'bodyFeature');
    }
    if (input.name === 'sittingType') {
      updateSittingStep3(input.value);
    }
    if (input.name === 'specialistType') {
      updateDynamicStep3('specialist', input.value, specialistStep3Config, 'specialist-question', 'specialist-step3-options', 'specialistFeature');
    }

    const nextStep = currentStep + 1;
    const answers = getFormData();
    if (currentStep === 2) {
      const showPref = shouldShowPreferenceStep(currentBranch, answers);
      setPreferenceStepVisible(showPref);
      if (!showPref) {
        pushQuizState(currentStep, currentBranch, true);
        showResults();
        return;
      }
    }
    if (nextStep > totalSteps) {
      pushQuizState(currentStep, currentBranch, true);
      showResults();
    } else {
      stepHistory.push(nextStep);
      pushQuizState(nextStep, currentBranch, false);
      showStep(nextStep);
    }
  }

  form.addEventListener('change', function(e) {
    if (e.target.type !== 'radio') return;
    handleRadioSelection(e.target);
  });

  form.addEventListener('click', function(e) {
    const option = e.target.closest('.pf-v2-option');
    if (!option) return;
    const input = option.querySelector('input[type="radio"]');
    if (!input) return;
    e.preventDefault();
    input.checked = true;
    handleRadioSelection(input);
  });
  
  backBtn.addEventListener('click', function() {
    if (stepHistory.length > 1) {
      stepHistory.pop();
      const prevStep = stepHistory[stepHistory.length - 1];
      pushQuizState(prevStep, currentBranch, false);
      showStep(prevStep);
    }
  });
  
  restartBtn.addEventListener('click', restart);

  if (skipBtn) {
    skipBtn.addEventListener('click', function() {
      showResults();
    });
  }


  resultsGrid.addEventListener('change', function(e) {
    if (!e.target.matches('[data-variant-select]')) return;
    const card = e.target.closest('.pf-v2-result-card');
    if (!card) return;
    const addBtn = card.querySelector('[data-action="add-to-cart"]');
    if (!addBtn) return;
    addBtn.dataset.variantId = e.target.value || '';
  });

  resultsGrid.addEventListener('click', async function(e) {
    const productLink = e.target.closest('a[href*="/products/"]');
    if (productLink) {
      track('finder_result_click', {
        destination: productLink.pathname || productLink.getAttribute('href')
      });
    }

    const btn = e.target.closest('[data-action="add-to-cart"]');
    if (!btn) return;
    const card = btn.closest('.pf-v2-result-card');
    const status = card ? card.querySelector('[data-atc-status]') : null;
    const variantId = Number(btn.dataset.variantId || 0);
    if (!variantId) {
      if (status) status.textContent = 'Please choose an option first.';
      return;
    }

    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding...';
    if (status) status.textContent = '';
    track('cart_add_attempt', {
      source: 'pillow_finder',
      variant_id: variantId,
      quantity: 1
    });

    try {
      const response = await fetchWithTimeout(`${storeRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!response.ok) throw new Error('add failed');
      /* Same fix as cart-drawer.js (21 Aug 2026): the finder runs on its own
         page, so page context has no product_sku and the Meta/Google
         listeners skipped these adds. The add.js response has the real sku. */
      var addedItem = await response.json().catch(function () { return {}; });
      track('cart_add_success', {
        source: 'pillow_finder',
        variant_id: variantId,
        quantity: 1,
        product_sku: addedItem.sku || undefined,
        product_price: (typeof addedItem.price === 'number')
          ? (addedItem.price / 100).toFixed(2) : undefined
      });
      if (status) status.textContent = themeStrings.finderAddedRedirecting;
      btn.textContent = 'Added';
      setTimeout(function() {
        window.location.href = `${storeRoot}cart`;
      }, 350);
    } catch (error) {
      track('cart_add_failure', {
        source: 'pillow_finder',
        variant_id: variantId,
        quantity: 1
      });
      if (status) status.textContent = 'Could not add now. Try again.';
      btn.textContent = original;
      btn.disabled = false;
    }
  });
  
  // Initialize
  // Push initial state so browser back button navigates within quiz
  history.replaceState({ quizStep: 1, quizBranch: 'root' }, '');

  function pushQuizState(step, branch, isResults) {
    history.pushState({ quizStep: step, quizBranch: branch, quizResults: !!isResults }, '');
  }

  window.addEventListener('popstate', function(e) {
    if (!e.state || !e.state.quizStep) return;
    if (e.state.quizResults) {
      // They went forward back to results — do nothing special
      return;
    }
    // Going back within the quiz
    if (results.style.display === 'block') {
      // Coming back from results to last quiz step
      results.style.display = 'none';
      form.style.display = 'block';
      currentStep = e.state.quizStep;
      currentBranch = e.state.quizBranch || currentBranch;
      showStep(currentStep);
      return;
    }
    // Normal step back
    if (stepHistory.length > 1) {
      stepHistory.pop();
    }
    currentStep = e.state.quizStep;
    currentBranch = e.state.quizBranch || currentBranch;
    showStep(currentStep);
  });

  showStep(1);

  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-quiz-wrapper]').forEach(initPillowFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); }, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
